import { Fragment, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Badge, Card, Col, Row } from "reactstrap";
import { useCurrency } from "../../_helper/CurrencyContext";
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';
import { AlertOctagon, CheckCircle, Clock } from "react-feather";
import { Breadcrumbs } from "../../AbstractElements";
import { useLicense } from "../../_helper/license/LicenseContext";
import { usePharmacy } from "../../_helper/PharmacyProvider";
import errorImage from "../.././assets/images/blank_profile.png"
import pharmacyErrImg from "../.././assets/images/hospital-placeholder.png"

import { formatDate, titleCase } from "../../utils/common";
import S3ImageLoader from "../../S3ImageLoader";
import { formatExpiryTime } from "../../utils/licenseDataAndExpiry";

const PackageDetails = () => {
    const { t } = useTranslation();
    const [subscriptionInfo, setSubscriptionInfo] = useState(null);

    const { getCurrencySymbol } = useCurrency();
    const { pharmacyPOS } = usePharmacy();
    const { decodeTokenOnly } = useLicense();

    const person = pharmacyPOS?.contact_person;
    const isHospitalConnected = pharmacyPOS?.isHospitalConnected;


    useEffect(() => {
        decodeTokenOnly().then((result) => {
            setSubscriptionInfo(result);
        })
    }, []);

    const InfoItem = ({ label, value }) => (
        <div className="mb-3">
            <span className="text-muted">{label}: </span>
            <span className="fw-semibold">{value || 'N/A'}</span>
        </div>
    );

    const subscriptionFeatures = subscriptionInfo
        ? Object.entries(subscriptionInfo.features || {})
        : [];


    return (
        <Fragment>
            <Breadcrumbs
                mainTitle={t('Package Benefits')}
                parent={t('Dashboard')}
                title={t('Package Benefits')}
            />

            <div className="container mt-4">
                <Row>
                    <Col xs="12">
                        <Card className="p-4">

                            <Row className="mb-4">
                                {/* ================= PERSONAL INFORMATION ================= */}
                                <Col md={12} lg={6}>
                                    <h5 className="fw-bold mb-3">{t('Personal Information')}</h5>
                                    <hr />
                                    <Row className="mt-3">
                                        <Col md={6} lg={4}>
                                            {/* <img
                                                src={logoPreview}
                                                alt="user profile"
                                                className="border border-5 border-white shadow-md"
                                                style={{ width: '160px', height: '160px', objectFit: 'cover' }}
                                                onError={(e) => { e.target.src = errorImage; }}
                                            /> */}
                                            <S3ImageLoader
                                                className="m-0 border border-5 border-white shadow-md"
                                                fileName={pharmacyPOS?.contact_person.profile_picture}
                                                style={{ width: '160px', height: '160px', objectFit: 'cover' }}
                                                alt="profile"
                                                fallbackSrc={errorImage}
                                            />
                                        </Col>
                                        <Col md={6} lg={8}>
                                            <InfoItem label="Full Name" value={person?.full_name} />
                                            <InfoItem label="Email" value={person?.contact_email} />
                                            <InfoItem label="Phone" value={`${person?.phone_code} ${person?.mobile}`} />
                                            <InfoItem label="Gender" value={titleCase(person?.gender || '')} />
                                        </Col>
                                    </Row>
                                </Col>
                                {/* ================= CLINICAL INFORMATION ================= */}
                                {/* {isHospitalConnected && ( */}
                                <Col md={12} lg={6}>
                                    <h5 className="fw-bold mb-3">{t('Pharmacy Information')}</h5>
                                    <hr />
                                    <Row className="mt-3">
                                        <Col md={6} lg={4}>
                                            <S3ImageLoader
                                                className="m-0 border border-5 border-white shadow-md"
                                                fileName={pharmacyPOS.logo}
                                                style={{ width: '160px', height: '160px', objectFit: 'cover' }}
                                                alt="profile"
                                                fallbackSrc={pharmacyErrImg}
                                            />
                                        </Col>
                                        <Col md={6} lg={8}>
                                            <InfoItem label="Pharmacy Name" value={pharmacyPOS?.facility_name} />
                                            <InfoItem label="Status" value={titleCase(pharmacyPOS?.status || '')} />
                                            <InfoItem label="Base Currency" value={pharmacyPOS?.baseCurrency} />
                                            <InfoItem label="License Key" value={subscriptionInfo?.license.key} />
                                        </Col>
                                    </Row>
                                </Col>
                                {/* )} */}

                            </Row>
                            <hr />
                            {/* ================= PACKAGE INFORMATION ================= */}
                            <div className="d-flex justify-content-between align-items-center">
                                <h5 className="fw-bold text-primary my-3">{t('Package Information')}</h5>
                            </div>

                            <div className="d-flex align-items-center mb-3">
                                <h5 className="mb-0 me-3">
                                    {subscriptionInfo?.plan?.name}
                                    <span className='ms-2 tw-text-sm tw-bg-[#1b255b] tw-text-[#ffc745] px-2 tw-rounded-xl'>{subscriptionInfo?.plan?.packageLevel}</span>
                                </h5>
                                {/* <span className="text-success fw-bold">
                                    {getCurrencySymbol()}{getInfo?.package?.price}
                                </span> */}
                            </div>

                            <Row>
                                {/* Activated On */}
                                <Col md={4} className="mb-2">
                                    <div className="d-flex align-items-start">
                                        <Clock size={16} className="text-primary me-2 mt-1" />
                                        <div>
                                            <div className="fw-semibold">
                                                {t("Activated On")}
                                            </div>
                                            <small className="text-muted">
                                                {subscriptionInfo ? formatDate(subscriptionInfo.activatedAt) : 'N/A'}
                                            </small>
                                        </div>
                                    </div>
                                </Col>
                                {/* Expire On */}
                                <Col md={4} className="mb-2">
                                    <div className="d-flex align-items-start">
                                        <AlertOctagon size={16} className="text-danger me-2 mt-1" />
                                        <div>
                                            <div className="fw-semibold">
                                                {t("Expire On")}
                                            </div>
                                            <small className="text-muted">
                                                {subscriptionInfo ? formatExpiryTime(subscriptionInfo.expiresAt) : 'N/A'}
                                            </small>
                                        </div>
                                    </div>
                                </Col>
                                <Col md={4} className="mb-2">
                                    <div className="d-flex align-items-start">
                                        <CheckCircle size={16} className="text-success me-2 mt-1" />
                                        <div>
                                            <div className="fw-semibold">
                                                {t("Staff Users Allowed")}
                                            </div>
                                            <small className="text-muted">
                                                {subscriptionInfo?.limitations?.users_allowed ?? "N/A"}
                                            </small>
                                        </div>
                                    </div>
                                </Col>

                                {/* Subscription features */}
                                {subscriptionFeatures.map(([key, value]) => (
                                    <Col md={4} key={key} className="mb-2">
                                        <div className="d-flex align-items-start">
                                            <CheckCircle size={16} className="text-success me-2 mt-1" />
                                            <div>
                                                <div className="fw-semibold">
                                                    {titleCase(key.replace(/_/g, " "))}
                                                </div>

                                                {typeof value !== "boolean" && (
                                                    <small className="text-muted">
                                                        ({t("Value")}: {titleCase(String(value))})
                                                    </small>
                                                )}
                                            </div>
                                        </div>
                                    </Col>
                                ))}
                            </Row>
                        </Card>
                    </Col>
                </Row>
            </div>
        </Fragment>

    )
}

export default PackageDetails;